import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

import * as actions from './actions';
import { useActions } from 'hooks/useActions';
import SearchBar from '../../components/SearchBar';
import Sorting from '../../components/Sorting';
import { Spinner } from 'components/Spinner/spinner';
import { Wrapper, Container } from '../../components';
import { contestDefaultThumbnail } from 'util/appConstants';
import ChallengeCard from './challengeCard';
import Filter from './filter';
import {
  Header,
  HeaderContainer,
  ContentContainer,
  LeftContainer,
  RightContainer,
  RightHeaderContainer,
  SortByLabel,
  ChallengeGrid,
} from '../../containers/ChallengeListPage/styles';

let defaultFilterOptions = {
  hasContests: true,
  noContests: false,
  isChallenge: true,
  isTournament: true,
};

export default React.memo(() => {
  const [challengesList, setChallengesList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState({
    value: 'createdAt',
    descendingOrder: true,
  });
  const [filterOptions, setFilterOptions] = useState(defaultFilterOptions);

  const { getAllChallengesForCMS } = useActions(actions);

  let { loading, challenges } = useSelector((state) => state.CMSAllChallengesReducer);

  const filterChallenges = useCallback(
    (challenge) => {
      if (
        filterOptions.isChallenge &&
        filterOptions.isTournament &&
        filterOptions.hasContests &&
        challenge.contestsCount > 0
      ) {
        return true;
      } else if (
        filterOptions.isChallenge &&
        filterOptions.isTournament &&
        filterOptions.noContests &&
        challenge.contestsCount === 0
      ) {
        return true;
      } else if (
        filterOptions.isChallenge &&
        !challenge.isTournament &&
        filterOptions.hasContests &&
        challenge.contestsCount > 0
      ) {
        return true;
      } else if (
        filterOptions.isChallenge &&
        !challenge.isTournament &&
        filterOptions.noContests &&
        challenge.contestsCount === 0
      ) {
        return true;
      } else if (
        filterOptions.isTournament &&
        challenge.isTournament &&
        filterOptions.hasContests &&
        challenge.contestsCount > 0
      ) {
        return true;
      } else if (
        filterOptions.isTournament &&
        challenge.isTournament &&
        filterOptions.noContests &&
        challenge.contestsCount === 0
      ) {
        return true;
      } else {
        return false;
      }
    },
    [filterOptions]
  );

  const sortFunc = useCallback(
    (a, b) => {
      let desc = 1;
      let asc = -1;
      if (String(a[sortBy.value]).toLowerCase() > String(b[sortBy.value]).toLowerCase()) {
        return sortBy.descendingOrder
          ? sortBy.value === 'createdAt'
            ? asc
            : desc
          : sortBy.value === 'createdAt'
          ? desc
          : asc;
      }
      if (String(a[sortBy.value]).toLowerCase() < String(b[sortBy.value]).toLowerCase()) {
        return sortBy.descendingOrder
          ? sortBy.value === 'createdAt'
            ? desc
            : asc
          : sortBy.value === 'createdAt'
          ? asc
          : desc;
      }
      return 0;
    },
    [sortBy]
  );

  useEffect(() => {
    getAllChallengesForCMS();
  }, [getAllChallengesForCMS]);

  useEffect(() => {
    setChallengesList(challenges.filter(filterChallenges).sort(sortFunc));
  }, [challenges, filterChallenges, sortFunc]);

  useEffect(() => {
    setChallengesList(challenges.filter(searchQueryFilter(searchQuery)).filter(filterChallenges).sort(sortFunc));
  }, [challenges, sortBy, searchQuery, filterOptions, filterChallenges, sortFunc]);

  const searchQueryFilter = (searchQuery) => (object) =>
    String(object.name).toLowerCase().indexOf(searchQuery.toLowerCase()) > -1;

  return (
    <Wrapper padding="60px 0 0 0" minHeight="100%">
      <Container column>
        <Header>Challenges</Header>
        <ContentContainer>
          <LeftContainer>
            <Filter
              filterOptions={filterOptions}
              defaultFilterOptions={defaultFilterOptions}
              clearFilters={() => setFilterOptions(defaultFilterOptions)}
              filterValues={(values) => setFilterOptions(values)}
            />
          </LeftContainer>
          <RightContainer>
            <HeaderContainer>
              <SearchBar value={searchQuery} onChange={setSearchQuery} />
              <RightHeaderContainer>
                <SortByLabel>Sort by:</SortByLabel>
                <Sorting
                  value={sortBy.value}
                  descendingOrder={sortBy.descendingOrder}
                  options={[
                    { value: 'name', label: 'Name' },
                    { value: 'isTournament', label: 'Category' },
                    { value: 'createdAt', label: 'Newest' },
                    { value: 'editedAt', label: 'Edited' },
                  ]}
                  onChange={(value) => setSortBy(value)}
                />
              </RightHeaderContainer>
            </HeaderContainer>
            {loading && <Spinner />}
            <ChallengeGrid>
              {challengesList.length >= 0 &&
                challengesList.map((challenge) => (
                  <ChallengeCard
                    key={challenge._id}
                    {...challenge}
                    image={
                      challenge?.backgroundImage?.thumbnail ??
                      challenge?.backgroundImage?.url ??
                      contestDefaultThumbnail
                    }
                    link={`/cms/allContests/${challenge._id}`}
                  />
                ))}
            </ChallengeGrid>
          </RightContainer>
        </ContentContainer>
      </Container>
    </Wrapper>
  );
});
